/**
 * Cloudinary Image Optimization Utility
 * 
 * USAGE: Only for final, approved images (hero, class photos, branded shots)
 * 
 * Settings:
 * - Auto format (WebP/AVIF for modern browsers)
 * - Auto quality (smart compression, no visible loss)
 * - NO auto-cropping - your composition stays exactly as shot
 * - Optional responsive width
 */

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'da3ouwgdr';

/**
 * Convert URL to Cloudinary-safe format
 * Cloudinary fetch requires the URL to be properly formatted
 */
function formatUrlForCloudinary(url) {
  // For URLs with special characters, we need to encode them properly
  // But not double-encode already-encoded URLs
  try {
    // Decode first in case it's already encoded, then re-encode properly
    const decoded = decodeURIComponent(url);
    // Only encode the parts that need it (spaces, special chars)
    return decoded.replace(/ /g, '%20');
  } catch (e) {
    return url;
  }
}

/**
 * Optimize an image URL through Cloudinary
 * 
 * @param {string} imageUrl - The original image URL
 * @param {object} options - Optional settings
 * @param {number} options.width - Max width (maintains aspect ratio)
 * @param {number} options.quality - Quality 1-100 (default: auto)
 * @returns {string} - Cloudinary-optimized URL
 * 
 * @example
 * // Basic optimization (recommended for most cases)
 * optimizeImage('https://example.com/hero.jpg')
 * 
 * // With max width for responsive (e.g., mobile)
 * optimizeImage('https://example.com/hero.jpg', { width: 800 })
 */
export function optimizeImage(imageUrl, options = {}) {
  if (!imageUrl) return '';
  
  // Skip if already a Cloudinary URL
  if (imageUrl.includes('cloudinary.com')) {
    return imageUrl;
  }
  
  // Skip data URLs and relative paths
  if (imageUrl.startsWith('data:') || !imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  // For now, return original URL if it's from customer-assets
  // (Cloudinary fetch has issues with some CDN domains)
  // The images are already served from a fast CDN
  if (imageUrl.includes('customer-assets.emergentagent.com')) {
    return imageUrl;
  }
  
  // Build transformation string
  const transforms = [
    'f_auto',      // Auto format (WebP, AVIF, etc.)
    'q_auto:good', // Auto quality, "good" preset (balanced)
  ];
  
  // Add width if specified (NO cropping - just max width)
  if (options.width) {
    transforms.push(`w_${options.width}`);
  }
  
  // Format URL for Cloudinary
  const formattedUrl = formatUrlForCloudinary(imageUrl);
  
  // Cloudinary fetch URL format
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/fetch/${transforms.join(',')}/${formattedUrl}`;
}

/**
 * Get responsive image srcSet for different screen sizes
 * Useful for hero images that need to look great on all devices
 * 
 * @param {string} imageUrl - The original image URL
 * @returns {object} - { src, srcSet, sizes }
 * 
 * @example
 * const { src, srcSet, sizes } = getResponsiveImage('https://example.com/hero.jpg');
 * <img src={src} srcSet={srcSet} sizes={sizes} />
 */
export function getResponsiveImage(imageUrl) {
  if (!imageUrl) return { src: '', srcSet: '', sizes: '' };
  
  const widths = [400, 800, 1200, 1600, 2000];
  
  const srcSet = widths
    .map(w => `${optimizeImage(imageUrl, { width: w })} ${w}w`)
    .join(', ');
  
  return {
    src: optimizeImage(imageUrl, { width: 1200 }), // Default/fallback
    srcSet,
    sizes: '(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px'
  };
}

export default {
  optimizeImage,
  getResponsiveImage,
  CLOUD_NAME
};
